import { Link } from 'react-router-dom';
import {
  Loader2,
  Building2,
  MapPin,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  ArrowRightCircle,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Company } from '@/modules/core/companies/types/company.types';
import { COMPANY_TYPE_CONFIG } from '@/modules/core/companies/types/company.types';

interface CompaniesTableProps {
  companies: Company[];
  isLoading?: boolean;
  onEdit?: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onConvertToClient?: (company: Company) => void;
}

export function CompaniesTable({
  companies,
  isLoading,
  onEdit,
  onDelete,
  onConvertToClient,
}: CompaniesTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-copper" />
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="border rounded-lg py-16 text-center text-muted-foreground">
        <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
        <p>Aucune société trouvée</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Société</TableHead>
            <TableHead>SIRET</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Ville</TableHead>
            <TableHead className="text-right">Opp. liées</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.map((company) => {
            const typeConfig = COMPANY_TYPE_CONFIG[company.companyType];
            const oppCount = company.opportunitiesCount ?? 0;

            return (
              <TableRow key={company.id} className="hover:bg-muted/30">
                <TableCell>
                  <Link
                    to={`/tm/companies/${company.id}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="p-1.5 bg-muted rounded-md shrink-0">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium group-hover:text-copper transition-colors">
                        {company.name}
                      </span>
                      {company.website && (
                        <div className="text-xs text-muted-foreground truncate">
                          {company.website.replace(/^https?:\/\//, '')}
                        </div>
                      )}
                    </div>
                  </Link>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground font-mono">
                  {company.siret || '—'}
                </TableCell>
                <TableCell>
                  <Badge className={`${typeConfig.bgColor} ${typeConfig.color}`}>
                    {typeConfig.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {company.city ? (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {company.postalCode ? `${company.postalCode} ` : ''}
                      {company.city}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {oppCount > 0 ? (
                    <Link
                      to={`/tm/companies/${company.id}?tab=opportunities`}
                      className="text-copper hover:underline"
                    >
                      {oppCount}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="flex flex-col gap-0.5">
                    {company.email && (
                      <a
                        href={`mailto:${company.email}`}
                        className="text-muted-foreground hover:text-copper truncate max-w-[180px]"
                      >
                        {company.email}
                      </a>
                    )}
                    {company.phone && (
                      <span className="text-muted-foreground">{company.phone}</span>
                    )}
                    {!company.email && !company.phone && (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/tm/companies/${company.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          Voir la fiche
                        </Link>
                      </DropdownMenuItem>
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(company)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                      )}
                      {company.website && (
                        <DropdownMenuItem asChild>
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Site web
                          </a>
                        </DropdownMenuItem>
                      )}
                      {onConvertToClient && company.companyType === 'prospect' && (
                        <DropdownMenuItem onClick={() => onConvertToClient(company)}>
                          <ArrowRightCircle className="h-4 w-4 mr-2" />
                          Passer en client
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => onDelete(company)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
